// src/hooks/useStats.js
import { useState, useEffect, useCallback } from 'react';
import { getMedal, isBetterMedal } from '../utils/medalUtils';

const STATS_KEY = 'wordamid-stats-v2';

const getTodayString = () => new Date().toISOString().split('T')[0];
const getYesterdayString = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
};

const defaultStats = {
    gamesPlayed: 0,
    wins: 0, // Legacy support
    medalCounts: {
        bronze: 0,
        silver: 0,
        gold: 0,
        platinum: 0
    },
    currentStreak: 0,
    maxStreak: 0,
    dailyResults: {},
};

// Removed local getMedal definition in favor of utils

export const useStats = () => {
    const [stats, setStats] = useState(defaultStats);

    // This useEffect runs once on game load to process previous day's results
    useEffect(() => {
        const loadAndProcessStats = () => {
            const savedStatsStr = localStorage.getItem(STATS_KEY);
            let newStats = savedStatsStr ? JSON.parse(savedStatsStr) : defaultStats;

            // Migration: Ensure medalCounts exists
            if (!newStats.medalCounts) {
                newStats.medalCounts = { bronze: 0, silver: 0, gold: 0, platinum: 0 };
            }

            const yesterdayStr = getYesterdayString();
            const yesterdayResult = newStats.dailyResults[yesterdayStr];
            
            // If yesterday's game was played but not won, it's now officially a loss.
            if (yesterdayResult && yesterdayResult.status === 'played') {
                yesterdayResult.status = 'lost';
                newStats.currentStreak = 0; // The streak is broken
            }

            // LEGACY MIGRATION: Convert old "won" games to "platinum" medals if no medal is set
            // Iterate through all daily results
            Object.keys(newStats.dailyResults).forEach(date => {
                const result = newStats.dailyResults[date];
                if (!result.medal && result.status === 'won') {
                    // Retroactively award platinum for wins
                    result.medal = 'platinum';
                    newStats.medalCounts.platinum = (newStats.medalCounts.platinum || 0) + 1;
                }
                // If status is 'lost' or 'played', we can't accurately determine medal 
                // unless we know the historical community best for that day.
                // Assuming we don't have historical community bests stored locally,
                // we leave them as is or treat as no medal.
                // Optionally, if `communityBestLength` WAS stored (from recent plays), we could recalc.
                if (!result.medal && result.communityBestLength && result.bestLength) {
                     const calculatedMedal = getMedal(result.bestLength, result.communityBestLength);
                     if (calculatedMedal) {
                         result.medal = calculatedMedal;
                         newStats.medalCounts[calculatedMedal] = (newStats.medalCounts[calculatedMedal] || 0) + 1;
                     }
                }
            });
            
            setStats(newStats);
            localStorage.setItem(STATS_KEY, JSON.stringify(newStats));
        };
        loadAndProcessStats();
    }, []);

    const updateStats = useCallback((newStats) => {
        setStats(newStats);
        localStorage.setItem(STATS_KEY, JSON.stringify(newStats));
    }, []);

    const updateMedalCounts = (currentStats, dateStr, newLength, communityBestLength) => {
        // If medals are locked for this day, do NOT update medals.
        if (currentStats.dailyResults[dateStr]?.medalsLocked) {
             return { updatedStats: currentStats, newMedal: currentStats.dailyResults[dateStr].medal };
        }

        // Calculate potential new medal based on current performance
        const calculatedMedal = getMedal(newLength, communityBestLength);
        
        // Get old medal for this day
        const oldResult = currentStats.dailyResults[dateStr];
        const oldMedal = oldResult ? oldResult.medal : null;

        // RATCHET LOGIC: Only update if the new medal is better than the old one
        // If we already have a medal, and the calculated one is worse (or same), keep the old one.
        // If we don't have a medal, take the calculated one.
        let newMedal = oldMedal;
        
        if (isBetterMedal(calculatedMedal, oldMedal)) {
            newMedal = calculatedMedal;
        } else if (!oldMedal) {
             newMedal = calculatedMedal;
        }

        if (newMedal !== oldMedal) {
             const updatedStats = { ...currentStats };
             
             // Ensure medalCounts structure exists
             if (!updatedStats.medalCounts) {
                 updatedStats.medalCounts = { bronze: 0, silver: 0, gold: 0, platinum: 0 };
             }

             // Decrement old medal count if it existed and count is positive
             if (oldMedal && updatedStats.medalCounts[oldMedal] > 0) {
                 updatedStats.medalCounts[oldMedal]--;
             }

             // Increment new medal count
             if (newMedal) {
                 // Check if the property exists before incrementing, initialize if missing
                 if (updatedStats.medalCounts[newMedal] === undefined) {
                      updatedStats.medalCounts[newMedal] = 0;
                 }
                 updatedStats.medalCounts[newMedal]++;
             }
             
             return { updatedStats, newMedal };
        }
        
        return { updatedStats: currentStats, newMedal: oldMedal }; // No change
    };

    const recordPlay = useCallback((bestLength, bestWord, starterWord, wordChain, date = null, communityBestLength = 0) => {
        const dateStr = date || getTodayString();
        if (stats.dailyResults[dateStr]) return; // Already recorded a play for this date

        let newStats = { ...stats };
        newStats.gamesPlayed += 1;
        
        // Initial play
        const { updatedStats, newMedal } = updateMedalCounts(newStats, dateStr, bestLength, communityBestLength);
        newStats = updatedStats;

        newStats.dailyResults[dateStr] = { 
            status: 'played', 
            bestLength, 
            bestWord, 
            starterWord, 
            wordChain,
            medal: newMedal,
            communityBestLength 
        };
        updateStats(newStats);
    }, [stats, updateStats]);

    const recordWin = useCallback((bestLength, bestWord, wordChain, date = null, communityBestLength = 0) => {
        const dateStr = date || getTodayString();
        const todayStr = getTodayString();
        const currentResult = stats.dailyResults[dateStr];
        
        if (currentResult && (currentResult.status === 'won' || currentResult.status === 'lost')) return;

        let newStats = { ...stats };
        if (!currentResult) { // First play of the day is a win
            newStats.gamesPlayed += 1;
        }

        newStats.wins += 1;
        
        // Only update streak if playing today's puzzle
        if (dateStr === todayStr) {
            newStats.currentStreak += 1;
            if (newStats.currentStreak > newStats.maxStreak) {
                newStats.maxStreak = newStats.currentStreak;
            }
        }
        
        const { updatedStats, newMedal } = updateMedalCounts(newStats, dateStr, bestLength, communityBestLength);
        newStats = updatedStats;

        newStats.dailyResults[dateStr] = { 
            ...newStats.dailyResults[dateStr], 
            status: 'won', 
            bestLength, 
            bestWord, 
            wordChain,
            medal: newMedal,
            communityBestLength
        };
        updateStats(newStats);
    }, [stats, updateStats]);
    
    const recordLoss = useCallback((bestLength, bestWord, wordChain, date = null, communityBestLength = 0) => {
        const dateStr = date || getTodayString();
        const todayStr = getTodayString();
        const currentResult = stats.dailyResults[dateStr];

        if (currentResult && currentResult.status !== 'played') return; // Can't lose if already won/lost

        let newStats = { ...stats };
        if (!currentResult) { // First play of the day is a loss (e.g. instant reveal)
             newStats.gamesPlayed += 1;
        }
        
        if (dateStr === todayStr) {
            newStats.currentStreak = 0;
        }
        
        // Update medal one last time (if not locked, but usually loss implies lock or end of game)
        const { updatedStats, newMedal } = updateMedalCounts(newStats, dateStr, bestLength, communityBestLength);
        newStats = updatedStats;

        newStats.dailyResults[dateStr] = { 
            ...newStats.dailyResults[dateStr], 
            status: 'lost', 
            bestLength, 
            bestWord, 
            wordChain,
            medal: newMedal,
            communityBestLength
        };
        updateStats(newStats);
    }, [stats, updateStats]);

    const freezeTodaysBest = useCallback((date = null) => {
        const dateStr = date || getTodayString();
        const currentResult = stats.dailyResults[dateStr];

        if (currentResult) {
            const newStats = { ...stats };
            newStats.dailyResults[dateStr].bestFrozen = true;
            updateStats(newStats);
        }
    }, [stats, updateStats]);
    
    // NEW: Function to lock medals
    const lockMedals = useCallback((date = null) => {
        const dateStr = date || getTodayString();
        const currentResult = stats.dailyResults[dateStr];
        
        if (currentResult && !currentResult.medalsLocked) {
             const newStats = { ...stats };
             newStats.dailyResults[dateStr].medalsLocked = true;
             updateStats(newStats);
        }
    }, [stats, updateStats]);

    const updateTodaysBest = useCallback((bestLength, bestWord, wordChain, date = null, communityBestLength = 0) => {
        const dateStr = date || getTodayString();
        const currentResult = stats.dailyResults[dateStr];

        // Only update if a record for the date exists, the new length is better, and the best is not frozen
        if (currentResult && !currentResult.bestFrozen && bestLength > (currentResult.bestLength || 0)) {
            let newStats = { ...stats };
            
            // Update medal logic
            const { updatedStats, newMedal } = updateMedalCounts(newStats, dateStr, bestLength, communityBestLength);
            newStats = updatedStats;

            newStats.dailyResults[dateStr].bestLength = bestLength;
            newStats.dailyResults[dateStr].bestWord = bestWord;
            newStats.dailyResults[dateStr].wordChain = wordChain;
            newStats.dailyResults[dateStr].medal = newMedal; // Store current medal
            newStats.dailyResults[dateStr].communityBestLength = communityBestLength;
            
            updateStats(newStats);
        }
    }, [stats, updateStats]);

    return { stats, recordPlay, recordWin, recordLoss, updateTodaysBest, freezeTodaysBest, lockMedals };
};
